import LogRocket from 'logrocket';
import _ from 'lodash';

import * as placeholderImgs from '../img';
import eventsPanelSettingsModal from './eventsPanelSettingsModal.component.js';
import template from './eventsPanel.html';


const FRESH = true;
const DEFAULT_GOOGLE_MAPS_ZOOM = 19;

const DEFAULT_ALERT_SOUND =
  'https://epicio-media-static-public.s3.us-east-1.amazonaws.com/epicio_new-event-notification.mp3';

// Exponential backoff schedule for the "click to enable sound"
// notification. Index = number of toasts shown so far (0-based, capped
// at the last entry). Values are milliseconds between toasts. We
// start gentle, ramp up so a user who ignores the prompt gets
// increasingly infrequent reminders rather than per-event spam.
const AUTOPLAY_BLOCKED_BACKOFF_MS = [
  0, // 1st failure: notify immediately
  60 * 1000, // 2nd failure: at least 1 min after previous
  5 * 60 * 1000, // 3rd: 5 min
  15 * 60 * 1000, // 4th: 15 min
  30 * 60 * 1000, // 5th and beyond: 30 min cap
];


export default {
  name: 'eventsPanel',
  template,
  controller: EventsPanelController,
  bindings: {
    panelId: '<',
  },
};

// TODO: when we convert to Angular
// Move all the filter logic out of the settings modal. It should only return a "settings configuration"
//  - Defining the filter property to be sent to the API should not be it's responsibility
//  - The settings modal should only be responsible for displaying the settings and returning the configuration
//  - The filter logic should be handled in the controller
//    or somewhere else, by using that configuration to define the filter


/* @ngInject */
function EventsPanelController(
  $rootScope,
  $scope,
  $q,
  $uibModal,
  growl,
  DashboardService,
  EventsPanelService,
  ModalService,
  ObserverService,
) {
  const vm = this;

  vm.panel = null;
  vm.playStates = {};
  vm.eventActionStates = {};
  vm.loadingSpinners = {
    stopEventsPanel: false,
  };
  vm.editPanel = {
    needsToBeConfigured: false,
  };
  vm.disableScrolledToEnd = true;
  vm.panelModel = null;
  vm.autoplayBlocked = {
    lastNotifiedAt: 0,
    toastCount: 0,
  };
  vm.img = placeholderImgs;
  vm.headerMenu = [
    {
      text: 'Settings',
      do: openSettingsModal,
    },
    {
      text: 'Restore Defaults',
      do: restorePanelDefaults,
    },
  ];
  vm.onEquipmentSelected = null;

  /** @type {Set<string>} Events from these equipment can use the pinpoint button */
  const eqWithMapPanel = new Set();


  /** Public Functions */
  vm.$onInit = init;
  vm.$postLink = setupEventListeners;
  vm.$onDestroy = removePanelModel;

  vm.fetchEvents = fetchEvents;
  vm.getEventImg = getEventImg;
  vm.pinpointThisEventOnMap = pinpointThisEventOnMap;
  vm.flagThisEvent = flagThisEvent;
  vm.deleteThisEvent = deleteThisEvent;
  vm.acknowledgeThisEvent = acknowledgeThisEvent;
  vm.removePanelModel = removePanelModel;
  vm.eventDeleted = eventDeleted;
  vm.shouldShowPinpointButton = shouldShowPinpointButton;
  vm.openDetailsModal = openDetailsModal;
  vm.openSettingsModal = openSettingsModal;
  vm.soundNotification = soundNotification;

  vm.vsRepeatOptions = {
    // `scrolledToEnd` now sends the scope into the function
    // We need to avoid this behaviour by wrapping `fetchEvents`
    scrolledToEnd: () => vm.fetchEvents(),
    // height of the row required to fix the issue with only one event showing up the first time
    size: 125,
    scrolledToEndOffset: 5,
  };

  function init() {
    vm.currentDashboard = DashboardService.getCurrentDashboard();
    vm.panel = vm.currentDashboard.panels[vm.panelId];

    getFilter();
    getSettings();

    if (_.isEmpty(vm.panel.equipment)) {
      vm.editPanel.needsToBeConfigured = true;
      vm.loadingSpinners.stopEventsPanel = true;
      return;
    }

    vm.panelModel = EventsPanelService.getModel(vm.panel.id);
    vm.disableScrolledToEnd = false;
    fetchEvents().then(() => {
      // Trigger digest after async fetch
      $scope.$evalAsync();
    });

    _(vm.currentDashboard.panels)
      .values()
      .filter({ type: 'equipmentMap' })
      .map('equipment')
      .flatten()
      .forEach(eq => eqWithMapPanel.add(eq));

    vm.onEquipmentSelected = $rootScope.$on('equipment-selected', () => handleFetchEvents());
  }

  /**
   * @param {boolean} [fresh=false] If fetch should *not* consider previous event dates (e.g.: applying new filter)
   * ! Note that a fresh request will clear the model
   * @returns {Promise} getEvents result or rejection if panel's scroll is disabled
   */
  function fetchEvents(fresh) {
    if (vm.disableScrolledToEnd) {
      return $q.reject();
    }

    vm.disableScrolledToEnd = true;

    const {
      panel,
      filter
    } = vm;
    return EventsPanelService
      .getEvents({
        panel,
        fresh,
        filter,
        settings: vm.settings,
      })
      .then((resultsMatchLimit) => {
        if (resultsMatchLimit) {
          // Assume that if the limit was matched then there are more events (25 + n)
          vm.disableScrolledToEnd = false;
        }
      })
      .finally(() => {
        updateCounts();
        vm.loadingSpinners.stopEventsPanel = true;
      });
  }

  function getEventImg(event) {
    return vm.img[event.equipment.equipmentType];
  }

  function pinpointThisEventOnMap(event) {
    ObserverService.publishAll('pinpoint', {
      event,
      options: { zoom: DEFAULT_GOOGLE_MAPS_ZOOM }
    });
  }

  function flagThisEvent(event) {
    EventsPanelService.flagThisEvent(event)
      .catch(() => {
        const error = 'Error updating event';
        growl.error(error);
        LogRocket.track(error);
      });
  }

  function acknowledgeThisEvent(event) {
    EventsPanelService.acknowledgeThisEvent(event)
      .catch((error) => {
        growl.error(error);
        LogRocket.track(error);
      });
  }

  function deleteThisEvent(event) {
    vm.eventActionStates[event.id] = true;

    EventsPanelService.deleteThisEvent(vm.panel.id, event)
      .catch(() => {
        const error = 'Error deleting event';
        growl.error(error);
        LogRocket.track(error);
        vm.eventActionStates[event.id] = false;
      });
  }

  function eventPassesFilter(event) {
    const { filter: filters } = vm;
    if (!filters) {
      return true;
    }

    if (filters?.hideAcknowledgedEvents && vm.isEventAcknowledged(event)) {
      return false;
    }

    if (filters?.showOnlyFlaggedEvents && !event.flagged) {
      return false;
    }

    const satisfiesArrayFilter = (itemToFind, filter) => _.isEmpty(filter) || filter.includes(itemToFind);
    const {
      eventName,
      equipment
    } = event;
    return satisfiesArrayFilter(equipment.id, filters.equipment) && satisfiesArrayFilter(eventName, filters.eventNames);
  }

  function handleNewEvent(event) {
    if (!eventPassesFilter(event)) {
      return;
    }

    const wasHandled = EventsPanelService.handleNewEvent(vm.panel, event, vm.settings);
    if (wasHandled) {
      updateCounts();
      soundNotification();
    }
  }

  function updateExistingEvent(event) {
    const result = EventsPanelService.updateExistingEvent(vm.panel.id, event, vm.settings);
    if (result) {
      updateCounts();
    }
    return result;
  }

  function eventDeleted(eventId) {
    if (EventsPanelService.eventDeleted(vm.panel.id, eventId)) {
      // These hash maps back UI interactions (spinner/flag playback). Drop the entry
      // when the event leaves the panel so we do not accumulate stale keys over time.
      delete vm.eventActionStates[eventId];
      delete vm.playStates[eventId];
      updateCounts();
    }
  }

  function shouldShowPinpointButton(event) {
    return eqWithMapPanel.has(event.equipment.id) && EventsPanelService.eventHasCoordinates(event);
  }

  function removePanelModel() {
    EventsPanelService.removePanelModel(vm.panel.id);
    if (_.isFunction(vm.onEquipmentSelected)) {
      // `$rootScope.$on` returns a deregister function; invoke it so the handler
      // stops listening once this panel is torn down, otherwise the root scope
      // keeps the panel model alive and leaks memory for long-lived dashboards.
      vm.onEquipmentSelected();
      vm.onEquipmentSelected = null;
    }
  }

  function setupEventListeners() {
    _.forEach(vm.panel.equipment, (equipmentId) => {
      $scope.$on(`event:${equipmentId}`, (__, event) => handleNewEvent(event));
    });

    $scope.$on('event:updated', (__, event) => {
      if (!vm.panel.equipment.includes(event.equipment.id)) {
        return;
      }

      // First check if the event exists in our panel model
      const eventIndex = _.findIndex(vm.panelModel.events, { id: event.id });

      // Check if this update is coming from tripwire panel
      const isTripwireSource = event._source === 'tripwirePanel';

      if (eventIndex === -1) {
        // Event doesn't exist in our model, add it if it passes filter
        handleNewEvent(event);
        $scope.$evalAsync();
      } else {
        // Event exists in our model, let's update it first
        const wasUpdated = updateExistingEvent(event);

        // If the event was specifically dismissed or is from tripwire, ensure the UI updates
        if (wasUpdated && (event.dismissed || isTripwireSource)) {
          // Force a digest cycle to ensure UI updates
          $scope.$evalAsync();
        }

        // If after update it no longer passes filter, remove it
        if (!eventPassesFilter(event)) {
          eventDeleted(event.id);
          $scope.$evalAsync();
        }
      }

      // Clean up our custom property if it exists
      if (event._source) {
        delete event._source;
      }
    });

    /* Event Deleted */
    $scope.$on('event:deleted', vm.eventDeleted);
  }

  function openDetailsModal(event) {
    ModalService.openEventDetailsModalSettings(event, vm.panelModel.events, vm.fetchEvents, vm.panel.id);
  }

  function handleAutoplayBlocked() {
    const now = Date.now();
    const state = vm.autoplayBlocked;
    const nextIndex = Math.min(state.failureCount, AUTOPLAY_BLOCKED_BACKOFF_MS.length - 1);
    const requiredGap = AUTOPLAY_BLOCKED_BACKOFF_MS[nextIndex];

    if (state.lastNotifiedAt !== 0 && (now - state.lastNotifiedAt) < requiredGap) {
      return;
    }

    state.failureCount += 1;
    state.lastNotifiedAt = now;
    growl.warning(
      'Click anywhere on the dashboard to enable sound notifications.',
      { title: 'Sound notifications paused', ttl: 15000 },
    );
  }

  async function soundNotification() {
    const { config = {} } = vm.panel || {};
    if (!config.newEventAlertsEnabled) {
      return;
    }

    const ua = navigator.userActivation;
    if (ua && ua.hasBeenActive === false) {
      handleAutoplayBlocked();
      return;
    }

    const soundUrl = config.newEventAlertsSoundUrl || DEFAULT_ALERT_SOUND;
    try {
      const audio = new Audio(soundUrl);
      await audio.play();
      vm.autoplayBlocked.failureCount = 0;
      vm.autoplayBlocked.lastNotifiedAt = 0;
    } catch (err) {
      if (err?.name === 'NotAllowedError') {
        handleAutoplayBlocked();
        return;
      }
      growl.error('Unable to play sound notification');
    }
  }

  async function openSettingsModal() {
    let modalResult;
    try {
      modalResult = await $uibModal.open({
        ...eventsPanelSettingsModal,
        controllerAs: '$modal',
        size: 'lg',
        resolve: {
          equipment() {
            return vm.panel.equipment;
          },
          initialFilters() {
            return _.cloneDeep(vm.filter);
          },
          requestee() {
            return vm.panel.id;
          },
          settings() {
            return vm.settings;
          }
        }
      }).result;
    } catch { /* Modal closed */ }

    if (modalResult?.restoredDefaults) {
      await restorePanelDefaults();
      return;
    }

    if (modalResult?.settings) {
      setSettings(modalResult.settings);
    }

    if (modalResult?.filters) {
      setFilter(modalResult.filters);

      // This assignment is a hack to coerce fetchEvents
      vm.disableScrolledToEnd = false;
      scrollTop();
      try {
        vm.loadingSpinners.stopEventsPanel = false;
        await fetchEvents(FRESH);
      } catch {
        growl.error(
          'We\'re unable to retrieve the events. Please refresh, try again later, or contact us for assistance',
          { title: 'We\'re sorry' }
        );
      }
    }

  }

  function scrollTop() {
    window.document.querySelector(`#scroll-${vm.panel.id}`).scrollTop = 0;
  }

  function getFilter() {
    const {
      availableEvents = [],
      defaultEvents = []
    } = vm.panel.config;

    const savedFilter = _.assign(EventsPanelService.getFilter(vm.panel.id), {
      availableEvents,
      defaultEvents
    });

    if (!EventsPanelService.hasFilters(savedFilter, ['eventNames']) && defaultEvents.length) {
      _.set(savedFilter, 'eventNames', [...defaultEvents]);
    }
    if (availableEvents.length) {
      _.set(savedFilter, 'eventNames', _.intersection(savedFilter.eventNames, availableEvents));
    }

    setFilter(savedFilter);
  }


  function setFilter(filter) {
    vm.filter = filter;
    vm.hasFilter = hasFilter();
  }

  function getSettings() {
    const settings = EventsPanelService.getSettings(vm.panel);
    setSettings(settings);
  }

  function setSettings(settings) {
    vm.settings = EventsPanelService.normalizeSettings(settings);
  }

  async function handleFetchEvents() {
    getFilter();
    vm.disableScrolledToEnd = false;
    await fetchEvents(true);
    scrollTop();
    // Digest after async fetch and filter update
    $scope.$evalAsync();
  }

  /**
   * Restore the panel to its configured defaults by clearing saved filters
   * and reapplying dashboard-level settings (persisting the reset).
   */
  async function restorePanelDefaults() {
    const panelId = vm.panel.id;
    const configDefaults = EventsPanelService.getSettingsFromConfig(vm.panel.config);

    try {
      vm.loadingSpinners.stopEventsPanel = false;

      await EventsPanelService.clearFilter(panelId, true);
      const restoredSettings = await EventsPanelService.setSettings(panelId, configDefaults, true);

      vm.hasFilter = false;
      setSettings(restoredSettings);

      await handleFetchEvents();
    } catch {
      growl.error('We could not restore this panel to its defaults. Please try again.', { title: 'Reset failed' });
    } finally {
      vm.loadingSpinners.stopEventsPanel = true;
    }
  }

  function hasFilter() {
    return EventsPanelService.hasFilters(vm.filter,
      [
        'equipment', 'eventNames', 'maxQueryDateRange',
        'hideAcknowledgedEvents', 'showOnlyFlaggedEvents',
      ]);
  }

  vm.isEventAcknowledged = function (item) {
    return item.data.eventAcknowledgedTime;
  };

  vm.filterFlaggedEvents = function (item) {
    return item.flagged;
  };

  vm.isEventAcknowledgeable = function (event) {
    return _.isEmpty(vm.panel.config?.acknowledgeableEvents) ||
      vm.panel.config?.acknowledgeableEvents.includes(event.eventName);
  };

  vm.shouldHighlightAckEvent = function (item) {
    return vm.isEventAcknowledgeable(item) && !vm.isEventAcknowledged(item);
  };

  vm.getFilteredCount = function () {
    return vm.panelModel.events.filter(vm.isEventAcknowledged).length;
  };

  vm.getNotAcknowledgedCount = function () {
    return vm.panelModel.events.filter(ev => vm.isEventAcknowledgeable(ev) && !vm.isEventAcknowledged(ev)).length;
  };

  function updateCounts() {
    vm.acknowledgedEventCount = vm.getFilteredCount();
    vm.pendingAcknowledgementCount = vm.getNotAcknowledgedCount();
  }

}
